// spell-prep-truth.ts — Lane L7 (2026-09-26): installs a committed `janus_spell_prep_truth` frame
// (options/game/spells/prepare_spells/spell_prep_truth_commit_inc.php) on every row this page holds for the caster — the
// encounter member row (encounter cache) and the caster cache rows (EC id / PC item id).
// Saved preparation and switch effects patch held clusters; only missing inputs use recovery.
// The committing tab runs the same installer on its answer; operation ids dedupe the websocket echo.
import { encounterCreatureField, encounterField, posCoord, type CasterArr } from '../shared/array-types.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { handleCasterUpdate } from './caster-update.js';
import { actorEncounterFor, actorRowFor, bankLegacyTruth, noteFrameSeen, pendingFor, type ConfirmedFields } from './movement-truth.js';
import type { GmBoundaryVerdict } from '../janus-engine/statuses/gm-boundary.js';
import { setRowAbilityCharges, rowAbilityChargeUpdates } from '../janus-engine/ability/charges/charge-entry.js';
import { applyMovementTruth, type MovementTruthRow } from '../janus-engine/movement/client-truth-commit.js';
import { normalizeOrbQueue } from '../janus-engine/ability/orbs/orb-queue.js';
import { compileFetchedEntity } from '../shared/compile-fetched-entity.js';
import { applyPreparedSpellStatusProjection, patchPreparedSpellProjection, planPreparedSpellStatuses } from '../janus-engine/compile/prepared-spell-delta';
import type { PreparedSpellDelta } from '../janus-engine/compile/prepared-spell-delta';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { FromBaseInputMissing } from '../janus-engine/compile/from-base-context';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { applyResourceValues, RESOURCE_KIND_COLUMNS, type ResourceValues } from '../janus-engine/resources/resource-adjust';
import { withAsyncDeadline } from '../shared/async-deadline';
import { storedStatusRows } from '../janus-engine/compile/creature-status-reader';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Row) : null);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const SCHEMA = 'janus.spell_prep.v1';
const resourceColumns = new Set<string>(Object.values(RESOURCE_KIND_COLUMNS).flat());

/** Only columns actually committed by this effect can replace confirmed movement truth. */
function effectMovementFields(changes: Row | undefined): ConfirmedFields {
  const fields: ConfirmedFields = {};
  for (const key of ['x_pos', 'y_pos', 'z_pos', 'movement_pct', 'aps'] as const)
    if (changes && key in changes) fields[key] = Number(encounterCreatureField(changes, key));
  return fields;
}

export interface SpellPrepFrame {
  schema: string; operation_id: string; encounter_id: string; actor_id: string; pc_item_id?: string;
  action: string; committed?: boolean; rev: number; sections: Row; catalog?: Row;
  /** Arcane Fatigue on a switch: the PHP ops (status_upsert / status_remove) the commit applied to the compiled rows. */
  status_ops?: Row[];
  effects?: GmBoundaryVerdict | null;
  status_ops_by_target?: Record<string, Row[]>;
  effect_revs?: Record<string, number>;
  base_refresh?: boolean;
}

/** Prepared sets are complete projections; effects remain independently ordered deltas.
 * A loaded snapshot covers both, while later movement/preparation revisions cover only their own fields. */
const actorNewest = new Map<string, number>();
// Effects are deltas: an intervening prepare/unprepare snapshot cannot supersede a switch's status write.
const effectNewest = new Map<string, number>();
const projectionRev = new WeakMap<object, number>();
// Full loaded snapshots cover every effect through their saved member revision.
// In-place movement/preparation deltas advance the row, but never this floor.
const loadedRevisions = new Map<string, number>();
const loadedRows = new WeakSet<object>();
function heldSpellPrepRows(): Set<Row> {
  const rows = new Set<Row>();
  for (const encounter of window.FateTide?.encounterCache?.values() ?? [])
    for (const raw of normalizePhpListish(asRow(encounter)?.encounter_creatures_arr)) {
      const row = asRow(raw);
      if (row) rows.add(row);
    }
  for (const raw of window.FateTide?.casterCache?.values() ?? []) {
    const row = asRow(raw);
    if (row) rows.add(row);
  }
  return rows;
}
function bankLoadedSpellPrepRow(row: Row): void {
  loadedRows.add(row);
  const id = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
  const rev = Number(encounterCreatureField(row.encounter_creatures, 'movement_rev') ?? 0);
  if (id && Number.isInteger(rev)) loadedRevisions.set(id, Math.max(loadedRevisions.get(id) ?? -1, rev));
}
function bankLoadedSpellPrepSnapshot(value: unknown): void {
  const host = asRow(value);
  if (!host) return;
  const rows = [host, ...normalizePhpListish(host.encounter_creatures_arr).flatMap(raw => {
    const row = asRow(raw); return row ? [row] : [];
  })];
  const ids = new Set(rows.map(row => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'))));
  for (const row of rows) bankLoadedSpellPrepRow(row);
  // The event carries the raw snapshot, before its cache aliases can move again.
  for (const row of heldSpellPrepRows())
    if (ids.has(upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')))) loadedRows.add(row);
}
function snapshotCovers(id: string, rev: number | undefined): boolean {
  return rev !== undefined && rev <= (loadedRevisions.get(upper(id)) ?? -1);
}
function snapshotCoversEffect(id: string, command: SpellPrepFrame): boolean {
  return snapshotCovers(id, command.effect_revs?.[id] ?? (id === upper(command.actor_id) ? command.rev : undefined));
}
function fresher(row: Row, rev: number): boolean {
  if (snapshotCovers(upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')), rev)) return false;
  const mark = projectionRev.get(row);
  if (mark !== undefined && rev < mark) return false;
  projectionRev.set(row, rev);
  return true;
}

/** The PHP `spell_prep` op, on one row (+ the catalog the row resolves from). */
export function applySpellPrepSections(row: Row, sections: Row, catalog: Row | null, catalogHost: Row): void {
  const ec = (asRow(row.encounter_creatures) ?? (row.encounter_creatures = {})) as Row;
  if ('prepared' in sections) {
    const p = asRow(sections.prepared);
    if (p && Object.keys(p).length) row.encounter_creatures_spells_prepared = p; else delete row.encounter_creatures_spells_prepared;
  }
  if (Number.isFinite(Number(sections.spell_list_prepared))) ec.spell_list_prepared = Math.trunc(Number(sections.spell_list_prepared));
  const rev = Number(sections.movement_rev);
  if (Number.isInteger(rev) && rev > Number(ec.movement_rev ?? 0)) ec.movement_rev = rev;
  const computed = (asRow(row.computed) ?? (row.computed = {})) as Row;
  const creature = (asRow(computed.creature) ?? (computed.creature = {})) as Row;
  if ('spells_known' in sections) {
    if (Array.isArray(sections.spells_known) && sections.spells_known.length) creature.spells_known = sections.spells_known; else delete creature.spells_known;
  }
  if (asRow(sections.castable) || Array.isArray(sections.castable)) creature.castable = sections.castable;
  // the re-stamped reach_watch: its zero-Vitality witness fact folds spells_known (finalize_creature_arr.php, 2026-10-08)
  if (asRow(sections.reach_watch)) creature.reach_watch = sections.reach_watch;
  if (Number.isFinite(Number(sections.max_spell_level_castable)) && sections.max_spell_level_castable !== null) {
    ((asRow(creature.spellcasting) ?? (creature.spellcasting = {})) as Row).max_spell_level_castable = Math.trunc(Number(sections.max_spell_level_castable));
  }
  if (catalog) {
    const host = (asRow(catalogHost.catalog) ?? (catalogHost.catalog = {})) as Row;
    for (const [id, def] of Object.entries(catalog)) if (!asRow(host[id])?.item_layer_types) host[id] = def;
  }
}

/** ft_inc_action_apply_ops' status_upsert (ft_status_upsert on target AND caster: merge by status_id, else append) and
 * status_remove (drop by status_id from both lists). */
export function applyStatusOps(row: Row, ops: readonly unknown[]): void {
  for (const raw of ops) {
    const op = asRow(raw);
    if (!op) continue;
    const statuses = (asRow(row.statuses) ?? (row.statuses = {})) as Row;
    if (op.op === 'status_upsert') {
      const entry = asRow(op.entry);
      const sid = upper(entry?.status_id);
      if (!entry || !sid) continue;
      for (const mode of Array.isArray(op.modes) ? op.modes : ['target', 'caster']) {
        if (mode !== 'target' && mode !== 'caster') continue;
        const list = storedStatusRows(row, mode);
        const at = list.findIndex(s => upper(asRow(s)?.status_id) === sid);
        if (at >= 0) list[at] = { ...(asRow(list[at]) ?? {}), ...entry }; else list.unshift({ ...entry });
        statuses[mode] = list;
      }
    } else if (op.op === 'status_remove') {
      const sid = upper(op.status_id);
      for (const mode of ['target', 'caster'] as const) {
        statuses[mode] = storedStatusRows(row, mode).filter(e => upper(readItemField(e, 'status_id')) !== sid);
      }
    }
  }
}

/** Every supported gain-hook write is delivered with the list, including writes addressed to another creature. */
export function applySpellPrepEffects(row: Row, frame: Pick<SpellPrepFrame, 'effects' | 'status_ops_by_target' | 'effect_revs' | 'catalog'>): void {
  const ec = asRow(row.encounter_creatures);
  if (!ec) return;
  const id = upper(encounterCreatureField(ec, 'encounter_creature_id')), effects = frame.effects;
  if (frame.catalog) Object.assign((row.catalog ??= {}) as Row, frame.catalog);
  // Status identity is independent of a creature's movement revision.
  applyStatusOps(row, frame.status_ops_by_target?.[id] ?? []);
  const rev = frame.effect_revs?.[id];
  if (rev !== undefined && Number(encounterCreatureField(ec, 'movement_rev') ?? 0) > rev) return;
  const changes = effects?.ec[id];
  if (changes) {
    for (const [key, value] of Object.entries(changes)) if (!['x_pos', 'y_pos', 'z_pos'].includes(key)) ec[key] = key === 'orb_queue' ? normalizeOrbQueue(value) : value;
    if (['x_pos', 'y_pos', 'z_pos'].some(key => key in changes)) {
      applyMovementTruth(row as never, { x_pos: Number(changes.x_pos ?? posCoord(row, 'x_pos') ?? 0), y_pos: Number(changes.y_pos ?? posCoord(row, 'y_pos') ?? 0), z_pos: Number(changes.z_pos ?? posCoord(row, 'z_pos') ?? 0) });
    }
  }
  for (const c of effects?.charges ?? []) if (upper(c.holder) === id) setRowAbilityCharges(row, upper(c.item_id), c.charges);
  const pc = asRow(row.player_characters);
  for (const c of effects?.playerCharactersChanged ?? []) if (pc && upper(c.item_id) === upper(encounterCreatureField(ec, 'item_id'))) pc[c.column] = c.after;
  if (rev !== undefined) ec.movement_rev = rev;
}

export function handleSpellPrepTruth(command: SpellPrepFrame): void {
  if (command?.schema !== SCHEMA || !Number.isInteger(command.rev) || !asRow(command.sections)) return;
  if (!noteFrameSeen(String(command.operation_id), 'committed')) return;
  for (const row of heldSpellPrepRows()) if (!loadedRows.has(row)) bankLoadedSpellPrepRow(row);
  // Scene subscriptions also feed the parent's held sheet; the router's AppliedFrame forwarding does not cover saved sections.
  try {
    if (window.parent !== window && window.frameElement?.matches('[data-ft3d-encounter-frame]'))
      window.parent.dispatchEvent(new CustomEvent('ft:encounter-wire-received', { detail: { source: window, frame: {
        type: 'janus_spell_prep_truth', truth: true, actor_id: command.actor_id, encounter_id: command.encounter_id,
        operation_id: command.operation_id, command,
      } } }));
  } catch { /* A cross-origin embed cannot share its parent's held sheet. */ }
  if (command.action === 'switch' && !('spells_known' in command.sections)) {
    void applySpellListSwitchDelta(command).catch(spellPrepRefreshFailed);
    return;
  }
  if (['prepare', 'unprepare', 'reset'].includes(command.action) && !('spells_known' in command.sections)) {
    void applyPreparedSpellDelta(command).catch(spellPrepRefreshFailed);
    return;
  }
  const actor = upper(command.actor_id);
  const encounterId = upper(command.encounter_id);
  const member = encounterId ? actorRowFor(encounterId, actor) : null;
  // Effects and sections can address the same object. Capture its saved revision before either installation.
  const before = member ? Number(encounterCreatureField(member.encounter_creatures, 'movement_rev') ?? 0) : 0;
  installSpellPrepEffects(command);
  if (snapshotCovers(actor, command.rev)) return;
  if ((actorNewest.get(actor) ?? -1) > command.rev) return;
  actorNewest.set(actor, command.rev);
  const sections = command.sections;
  const catalog = asRow(command.catalog);

  if (member && fresher(member as unknown as Row, command.rev)) {
    const enc = (actorEncounterFor(encounterId) as unknown as Row | null) ?? (member as unknown as Row);
    applySpellPrepSections(member as unknown as Row, sections, catalog, enc);
    applyStatusOps(member as unknown as Row, command.status_ops ?? []);
    // The per-actor revision every client-truth lane orders by: bank it so the next walk / action CASes the right value.
    if (command.rev > before && pendingFor(encounterId, actor) === 0) bankLegacyTruth(encounterId, actor, member, command.rev,
      effectMovementFields(command.effects?.ec[actor]), 'frame');
  }
  const cache = (window as unknown as { FateTide?: { casterCache?: Map<string, unknown> } }).FateTide?.casterCache;
  for (const id of new Set([actor, upper(command.pc_item_id)])) {
    if (!id) continue;
    for (const [key, value] of cache?.entries?.() ?? []) {
      const row = asRow(value);
      if (upper(key) !== id || !row || !fresher(row, command.rev)) continue;
      applySpellPrepSections(row, sections, catalog, row);
      applyStatusOps(row, command.status_ops ?? []);
      handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
    }
  }
  window.dispatchEvent(new CustomEvent('ft:spell-prep-truth', { detail: command }));
  // Floor inventory is installed by the native raw-state reader/compile.
  if (encounterId && command.effects?.inventory?.length)
    void window.ftResyncEncounter?.(encounterId);
  if (command.base_refresh) void refreshSpellPrepBase(command).catch(spellPrepRefreshFailed);
}


/** Recheck effect ordering after asynchronous input joins as well as on receipt. */
function orderedSpellPrepEffects(command: SpellPrepFrame): SpellPrepFrame {
  if (!command.effects) return command;
  const actor = upper(command.actor_id);
  const fresh = (key: string): boolean => {
    const version = actor + ':' + key;
    if ((effectNewest.get(version) ?? -1) > command.rev) return false;
    effectNewest.set(version, command.rev); return true;
  };
  return { ...command,
    status_ops_by_target: Object.fromEntries(Object.entries(command.status_ops_by_target ?? {}).map(([id, ops]) => [id,
      ops.filter(op => fresh('status:' + upper(asRow(op.entry)?.status_id ?? op.status_id)))])),
    effects: { ...command.effects,
      charges: command.effects.charges.filter(c => fresh(`charge:${upper(c.holder)}:${upper(c.item_id)}`)),
      playerCharactersChanged: command.effects.playerCharactersChanged.filter(c => fresh(`pc:${upper(c.item_id)}:${c.column}`)),
    },
  };
}

/** Shared receiver for legacy derived frames and current saved-row switches. */
function installSpellPrepEffects(command: SpellPrepFrame, scoped = false): { changed: Map<string, string[]>; rawUpdates: { id: string; frame: SpellPrepFrame }[]; recoveries: Map<string, string> } {
  const actor = upper(command.actor_id), encounterId = upper(command.encounter_id);
  const changed = new Map<string, string[]>(), rawUpdates: { id: string; frame: SpellPrepFrame }[] = [], recoveries = new Map<string, string>();
  if (command.effects) {
    const ordered = orderedSpellPrepEffects(command);
    const touched = new Set([...Object.keys(ordered.effects!.ec), ...Object.keys(ordered.status_ops_by_target ?? {}), ...ordered.effects!.charges.map(c => upper(c.holder))]);
    const pcItems = new Set(ordered.effects!.playerCharactersChanged.map(c => upper(c.item_id)));
    const painted = new Map<string, Row>();
    const rows = heldSpellPrepRows();
    const plans = new Map<string, { source: Row; projection: ReturnType<typeof planPreparedSpellStatuses> }>();
    // Decide once per member before any alias can mutate a shared status bucket.
    if (scoped) for (const row of rows) {
      const ec = row.encounter_creatures, id = upper(encounterCreatureField(ec, 'encounter_creature_id'));
      if (snapshotCoversEffect(id, ordered)) continue;
      if (plans.has(id) || !touched.has(id) && !pcItems.has(upper(encounterCreatureField(ec, 'item_id')))) continue;
      const rowCatalog = asRow(row.catalog) ?? {}, catalog = asRow(actorEncounterFor(encounterId)?.catalog) ?? rowCatalog;
      let projection = { row, paths: [] as string[] };
      try { projection = planPreparedSpellStatuses(row, ordered.status_ops_by_target?.[id] ?? [], catalog,
        () => normalizePhpListish(actorEncounterFor(encounterId)?.encounter_creatures_arr),
        key => asRow(ordered.catalog?.[key]) ?? asRow(rowCatalog[key]) ?? asRow(catalog[key])); }
      catch (error) {
        if (!spellPrepMissingInput(error)) throw error;
        recoveries.set(id, String(asRow(error)?.message));
      }
      plans.set(id, { source: row, projection });
    }
    const rawIds = new Set<string>();
    const install = (row: Row): void => {
      const ec = row.encounter_creatures, id = upper(encounterCreatureField(ec, 'encounter_creature_id'));
      if (snapshotCoversEffect(id, ordered)) return;
      if (!touched.has(id) && !pcItems.has(upper(encounterCreatureField(ec, 'item_id')))) return;
      const priorRev = Number(encounterCreatureField(ec, 'movement_rev') ?? 0);
      let paths: string[] | undefined;
      if (scoped) {
        const { source, projection: projected } = plans.get(id)!;
        paths = [...projected.paths, ...(ordered.status_ops_by_target?.[id]?.length ? ['statuses.target', 'statuses.caster'] : []),
          ...Object.keys(ordered.effects?.ec[id] ?? {}).map(key => `encounter_creatures.${key}`)];
        if (projected.row !== source) applyPreparedSpellStatusProjection(row, projected);
        for (const charge of ordered.effects?.charges ?? []) if (upper(charge.holder) === id) {
          const patch = rowAbilityChargeUpdates(row, upper(charge.item_id), charge.charges);
          paths.push(...patch.set.map(change => change.path.join('.')), ...patch.unset.map(path => path.join('.')));
        }
        if (pcItems.has(upper(encounterCreatureField(ec, 'item_id'))))
          recoveries.set(id, 'spell-prep:player-character-effect-clusters');
        changed.set(id, [...new Set([...changed.get(id) ?? [], ...paths])]);
        rawIds.add(id);
      }
      applySpellPrepEffects(row, ordered);
      if (scoped && (ordered.effect_revs?.[id] === undefined || priorRev <= ordered.effect_revs[id]!)) {
        const resources: ResourceValues = {};
        for (const key of Object.keys(ordered.effects?.ec[id] ?? {})) if (resourceColumns.has(key))
          (resources as Row)[key] = Number(encounterCreatureField(ordered.effects?.ec[id], key));
        applyResourceValues(row, resources);
        if (Object.keys(resources).some(key => key.startsWith('v_'))) changed.get(id)?.push('computed.vitality');
      }
      const effectRev = ordered.effect_revs?.[id];
      if (id !== actor && effectRev !== undefined && effectRev > priorRev && pendingFor(encounterId, id) === 0)
        bankLegacyTruth(encounterId, id, row as unknown as MovementTruthRow, effectRev,
          effectMovementFields(ordered.effects?.ec[id]), 'frame');
      if (!painted.has(id)) painted.set(id, row);
    };
    for (const row of rows) install(row);
    for (const cached of window.FateTide?.encounterCache?.values() ?? []) {
      const encounter = asRow(cached), eid = upper(encounterField(encounter, 'encounter_id'));
      const ops = ordered.status_ops_by_target?.[eid] ?? [];
      if (encounter && ops.length) {
        if (ordered.catalog) Object.assign((encounter.catalog ??= {}) as Row, ordered.catalog);
        applyStatusOps(encounter, ops);
        window.dispatchEvent(new CustomEvent('ft:encounter-state-received', { detail: { encounterId: eid, encounterArr: encounter, subType: 'spell_prep', changedCreatureIds: [] } }));
      }
      const removed = normalizePhpListish(encounter?.encounter_creatures_arr).filter(raw => {
        const ec = asRow(raw)?.encounter_creatures, id = upper(encounterCreatureField(ec, 'encounter_creature_id'));
        return ordered.effects?.ec[id]?.encounter_creature_deleted === 1
          && Number(encounterCreatureField(ec, 'encounter_creature_deleted') ?? 0) === 1;
      });
      if (encounter && removed.length) {
        const ids = removed.map(raw => upper(encounterCreatureField(asRow(raw)?.encounter_creatures, 'encounter_creature_id')));
        encounter.encounter_creatures_arr = normalizePhpListish(encounter.encounter_creatures_arr).filter(raw => !removed.includes(raw));
        window.dispatchEvent(new CustomEvent('ft:encounter-rows-patched', { detail: { encounterId: eid, removedIds: ids } }));
      }
    }
    if (scoped) {
      for (const [id, row] of painted) window.FateTide?.casterCache?.set(id, row as unknown as CasterArr);
      window.ftPatchAppliedCasterViews?.(changed, '');
    } else for (const row of painted.values()) handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
    if (scoped) for (const id of rawIds) rawUpdates.push({ id, frame: ordered });
  }
  return { changed, rawUpdates, recoveries };
}

/** Persisted facts update only their raw rows; definitions remain owned by the held input bridge. */
function savedSpellPrepEffects(base: Row, command: SpellPrepFrame): Row {
  if (snapshotCoversEffect(upper(encounterCreatureField(base.encounter_creatures, 'encounter_creature_id')), command)) return base;
  command = orderedSpellPrepEffects(command);
  const row = { ...base, encounter_creatures: { ...asRow(base.encounter_creatures) },
    statuses: { ...asRow(base.statuses), target: [...storedStatusRows(base, 'target')], caster: [...storedStatusRows(base, 'caster')] },
    ...(asRow(base.player_characters) ? { player_characters: { ...asRow(base.player_characters) } } : {}) };
  const id = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
  applySpellPrepEffects(row, { ...command, catalog: undefined });
  if ((command.effect_revs?.[id] === undefined || Number(encounterCreatureField(base.encounter_creatures, 'movement_rev') ?? 0) <= command.effect_revs[id]!)
      && command.effects?.charges.some(charge => upper(charge.holder) === id)) {
    const charges = normalizePhpListish(base.charges).map(raw => asRow(raw) ?? {});
    for (const change of command.effects.charges) if (upper(change.holder) === id) {
      const at = charges.findIndex(charge => upper(readItemField(charge, 'item_id')) === upper(change.item_id));
      const entry = { ...charges[at], charge_holder: id, item_id: change.item_id, charges: change.charges };
      if (at < 0) charges.push(entry); else charges[at] = entry;
    }
    (row as Row).charges = charges;
  }
  return row;
}

async function applySpellListSwitchDelta(command: SpellPrepFrame): Promise<void> {
  const actor = upper(command.actor_id);
  actorNewest.set(actor, Math.max(actorNewest.get(actor) ?? -1, command.rev));
  const member = actorRowFor(upper(command.encounter_id), upper(command.actor_id));
  const before = member ? Number(encounterCreatureField(member.encounter_creatures, 'movement_rev') ?? 0) : undefined;
  const { rawUpdates, recoveries } = installSpellPrepEffects(command, true);
  const patch = window.ftPatchPreparedSpellDelta;
  try {
    if (patch) for (const { id, frame } of rawUpdates) await withAsyncDeadline(signal => patch(id, {}, () => !signal.aborted, undefined, {
      patch: base => savedSpellPrepEffects(base, frame),
      definitions: (frame.status_ops_by_target?.[id] ?? []).flatMap(op => op.op === 'status_upsert' ? [upper(readItemField(asRow(op.entry), 'status_item_id'))] : []),
    }), 20_000);
  } catch (error) {
    if (asRow(error)?.name === 'TimeoutError') throw new Error('spell-prep:saved-inputs-timeout');
    throw error;
  }
  for (const [id, reason] of recoveries) {
    await refreshSpellPrepBase(command, [id]);
    window.dispatchEvent(new CustomEvent('ft:spell-prep-effects-recovered', { detail: { operationId: command.operation_id, actorId: id, recovery: reason } }));
  }
  await applyPreparedSpellDelta(command, before);
  if (command.encounter_id && command.effects?.inventory?.length) {
    // This legacy effect tail omits the saved inventory revision. Recover only
    // that facet, rather than rereading the actor and its entire encounter.
    const envelope = await withAsyncDeadline(async signal => {
      const response = await fetch(`/options/api/base_entity_inc.php?entity_id=${encodeURIComponent(command.encounter_id)}&entity=inventory&cold=0`, { signal });
      if (!response.ok) throw new Error(`spell-prep:inventory-base-read:${response.status}`);
      return response.json();
    }, 20_000);
    await withAsyncDeadline(() => compileFetchedEntity({ _inventory_base: envelope }), 20_000);
    window.dispatchEvent(new CustomEvent('ft:spell-prep-effects-recovered', { detail: { operationId: command.operation_id,
      actorId: command.encounter_id, recovery: 'spell-prep:inventory-saved-revision-missing' } }));
  }
}

function spellPrepMissingInput(error: unknown): boolean {
  const missing = asRow(error);
  return error instanceof FromBaseInputMissing || typeof missing?.dependency === 'string' && typeof missing.id === 'string'
    && missing.message === `from-base-input-missing:${missing.dependency}:${missing.id}`;
}

function spellPrepRefreshFailed(error: unknown): void {
  console.error('[spell-prep] browser compile failed', error);
  window.dispatchEvent(new CustomEvent('ft:spell-prep-refresh-refused', { detail: { reason: String(asRow(error)?.message ?? error) } }));
  window.displayWarning?.('The spells were saved, but their display could not be refreshed. Try opening the sheet again.');
}

function preparedSpellSurfaces(actor: string, encounterId: string, pcItemId?: string): Set<Row> {
  const rows = new Set<Row>();
  const member = encounterId ? actorRowFor(encounterId, actor) : null;
  if (member) rows.add(member as unknown as Row);
  for (const [key, value] of window.FateTide?.casterCache?.entries() ?? []) {
    if (upper(key) === actor || upper(key) === upper(pcItemId)
      || upper(encounterCreatureField(value.encounter_creatures, 'encounter_creature_id')) === actor) rows.add(value as unknown as Row);
  }
  return rows;
}

/** HTTP answer and websocket observers share the same read-only plan and section installation. */
export async function applyPreparedSpellDelta(command: SpellPrepFrame, originalRevision?: number): Promise<void> {
  const actor = upper(command.actor_id), encounterId = upper(command.encounter_id);
  if (snapshotCovers(actor, command.rev)) return;
  if ((actorNewest.get(actor) ?? -1) > command.rev) return;
  actorNewest.set(actor, command.rev);
  // The local sink saved and published this encounter before returning the frame.
  // Confirm its held projection without rereading or recompiling the same preparation.
  if (command.action !== 'switch' && ftCurrentTruthSink()?.heldBase?.(encounterId)) {
    window.dispatchEvent(new CustomEvent('ft:spell-prep-truth', { detail: command }));
    return;
  }
  const start = performance.now();
  let delta: Omit<PreparedSpellDelta, 'saved'> | undefined;
  let recovery: string | undefined;
  const current = (): boolean => !snapshotCovers(actor, command.rev) && (actorNewest.get(actor) ?? command.rev) <= command.rev;
  try {
    delta = await window.ftPatchPreparedSpellDelta?.(actor, command.sections, current);
    if (!delta && current()) {
      // Scene frames can hold only the projection; their parent owns its raw
      // inputs. Borrow a read-only section plan for this frame's own before-state.
      const projection = preparedSpellSurfaces(actor, encounterId, command.pc_item_id).values().next().value;
      let parentPatch: Window['ftPatchPreparedSpellDelta'];
      try { if (window.parent && window.parent !== window) parentPatch = window.parent.ftPatchPreparedSpellDelta; }
      catch { /* Cross-origin frames retain their own recovery path. */ }
      if (projection && parentPatch) delta = await parentPatch(actor, command.sections, current, projection);
    }
  }
  catch (error) {
    // A pre-delta cached projection lacks the grant provenance, or a dependency is absent.
    // Parent-realm errors have a different constructor; retain the same named recovery.
    if (!spellPrepMissingInput(error)) throw error;
    recovery = String(asRow(error)?.message);
  }
  if (!current()) return;
  if (!delta) {
    // Campaign subscriptions also reach surfaces without this actor. Without
    // either a raw/member pair or a display projection, there is nothing to refresh.
    if (!recovery && !preparedSpellSurfaces(actor, encounterId, command.pc_item_id).size) return;
    await refreshSpellPrepBase(command, [actor]);
    window.dispatchEvent(new CustomEvent('ft:spell-prep-truth', { detail: command }));
    window.dispatchEvent(new CustomEvent('ft:spell-prep-delta-applied', { detail: {
      operationId: command.operation_id, actorId: actor, patched: false, recovery: recovery ?? 'held-preparation-inputs-missing', ms: performance.now() - start } }));
    return;
  }
  const member = encounterId ? actorRowFor(encounterId, actor) : null;
  const beforeRevision = originalRevision ?? (member ? Number(encounterCreatureField(member.encounter_creatures, 'movement_rev') ?? 0) : 0);
  const rows = preparedSpellSurfaces(actor, encounterId, command.pc_item_id);
  const catalog = asRow((actorEncounterFor(encounterId) as unknown as Row | null)?.catalog);
  const changedSpells = new Set<string>();
  const spellIds = (value: unknown): Set<string> => new Set(normalizePhpListish(value).map(raw => upper(readItemField(asRow(raw), 'item_id') ?? raw)));
  const afterSpells = spellIds(delta.sections.spells_known);
  for (const row of rows) {
    if (!fresher(row, command.rev)) continue;
    const beforeSpells = spellIds(asRow(asRow(row.computed)?.creature)?.spells_known);
    for (const id of beforeSpells) if (!afterSpells.has(id)) changedSpells.add(id);
    for (const id of afterSpells) if (!beforeSpells.has(id)) changedSpells.add(id);
    const patched = patchPreparedSpellProjection(row, delta);
    row.encounter_creatures = patched.encounter_creatures; row.computed = patched.computed;
    if ('encounter_creatures_spells_prepared' in patched) row.encounter_creatures_spells_prepared = patched.encounter_creatures_spells_prepared;
    else delete row.encounter_creatures_spells_prepared;
    const host = asRow(row.catalog) ?? catalog ?? (row.catalog = {}) as Row;
    for (const [id, def] of Object.entries(delta.catalog)) if (!asRow(host[id])?.item_layer_types) host[id] = def;
  }
  if (member && command.rev > beforeRevision && pendingFor(encounterId, actor) === 0) bankLegacyTruth(encounterId, actor, member, command.rev, effectMovementFields(command.effects?.ec[actor]), 'frame');
  const ids = new Set([actor, upper(command.pc_item_id)].filter(Boolean));
  if (changedSpells.size) for (const id of ids) (window as Window & { ftPatchPcSpellPanes?: (id: string, spells: readonly string[]) => void })
    .ftPatchPcSpellPanes?.(id, [...changedSpells]);
  window.dispatchEvent(new CustomEvent('ft:spell-prep-truth', { detail: command }));
  window.dispatchEvent(new CustomEvent('ft:spell-prep-delta-applied', { detail: {
    operationId: command.operation_id, actorId: actor, patched: true, memberCompiles: 0, rawReads: 0, wholeCopies: 0,
    spellCompiles: delta.spellCompiles, changedPaths: ['encounter_creatures.spell_list_prepared', 'encounter_creatures_spells_prepared',
      ...(delta.spellCompiles ? ['computed.creature.spells_known', 'computed.creature.castable.spells_known', 'computed.creature.reach_watch', 'computed.creature.spellcasting.max_spell_level_castable'] : [])], rows: rows.size, ms: performance.now() - start } }));
}

/** Use saved inputs, including a cold or encounterless PC, through the normal compiler/write-back owner. */
async function refreshSpellPrepBase(command: SpellPrepFrame, participants = [upper(command.actor_id), ...Object.keys(command.effect_revs ?? {})]): Promise<void> {
  const superseded = (): boolean => (actorNewest.get(upper(command.actor_id)) ?? command.rev) > command.rev;
  if (superseded()) return;
  const ids = new Set(participants
    .filter(id => Number(encounterCreatureField(command.effects?.ec[id], 'encounter_creature_deleted') ?? 0) !== 1));
  await Promise.all([...ids].map(async id => {
    const envelope = await withAsyncDeadline(async signal => {
      const response = await fetch(`/options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=creature&cold=0`, { signal });
      if (!response.ok) throw new Error(`spell-prep:base-read:${response.status}`);
      return response.json();
    }, 20_000);
    if (superseded()) return;
    let row: Row;
    try { row = await withAsyncDeadline(() => compileFetchedEntity({ _entity_base: envelope }), 20_000); }
    catch (error) {
      if (asRow(error)?.name === 'TimeoutError') throw new Error('spell-prep:base-adoption-timeout');
      throw error;
    }
    // A later saved frame supersedes an in-flight read. Its refresh owns the projection.
    if (superseded()) return;
    handleCasterUpdate({ type: 'caster_update', caster_arr: row as unknown as CasterArr, local: true });
    const cache = window.FateTide?.casterCache;
    const fresh = cache?.get(id) ?? row as unknown as CasterArr;
    // A PC item id and its EC id can differ. Every held sheet alias must see the same fresh spell projection.
    for (const [key, held] of cache?.entries() ?? [])
      if (upper(encounterCreatureField(held.encounter_creatures, 'encounter_creature_id')) === id) cache?.set(key, fresh);
    if (id === upper(command.actor_id) && command.pc_item_id) cache?.set(upper(command.pc_item_id), fresh);
  }));
}

Object.assign(window, { ftApplySpellPrepFrame: handleSpellPrepTruth });
window.addEventListener('ft:encounter-installed', event => {
  bankLoadedSpellPrepSnapshot(asRow((event as CustomEvent).detail)?.encounterArr);
});
window.addEventListener('ft:entity-base-compiled', event => {
  const detail = asRow((event as CustomEvent).detail);
  if (detail?.applied !== true) bankLoadedSpellPrepSnapshot(detail?.computed);
});
